import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { HeroBlog } from "../constants";

export type BlogPostSimple = {
  updatedAt?: string;
  id: number;
  name: string;
  slug: string;
  mainImage: {
    id: number;
    alt: string;
    url: string;
    filename?: string;
    mimeType?: string;
    filesize?: number;
    width?: number;
    height?: number;
  };
  category: {
    id: number;
    name: string;
    slug: string;
  };
  date: string;
  readTime: string;
};

export type Category = {
  id: number;
  name: string;
  slug: string;
};

export type BlogPartner = {
  id: string;
  image: {
    id: number;
    alt: string;
    url: string;
    filename?: string;
    mimeType?: string;
    filesize?: number;
    width?: number;
    height?: number;
  };
  link: string;
};

interface BlogHomeData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  homeFeaturedBlogs: HeroBlog[];
  sidebarFeaturedBlogs: BlogPostSimple[];
  latestBlogs: BlogPostSimple[];
  categories: Category[];
  activeCategory: string;
  activeCategorySlug: string;
  categoryBlogs: BlogPostSimple[];
  categoryBlogsLoading: boolean;
  blogPartners: BlogPartner[];
  totalDocs: number;
  globalInventories: any[];
  error: any;
}

export const initialState: BlogHomeData = {
  readyStatus: "invalid",
  homeFeaturedBlogs: [],
  sidebarFeaturedBlogs: [],
  latestBlogs: [],
  categories: [],
  activeCategory: "latest",
  activeCategorySlug: "",
  categoryBlogs: [],
  categoryBlogsLoading: false,
  blogPartners: [],
  totalDocs: 0,
  globalInventories: [],
  error: null,
};

const blogHomeSlice = createSlice({
  name: "blogHomeData",
  initialState,
  reducers: {
    updateBlogHomeData: (state: BlogHomeData, { payload }: PayloadAction<any>) => {
      state.homeFeaturedBlogs = payload?.homeFeaturedBlogs || [];
      state.sidebarFeaturedBlogs = payload?.sidebarFeaturedBlogs || [];
      state.latestBlogs = payload?.latestBlogs || [];
      state.categories = payload?.categories || [];
      state.blogPartners = payload?.blogPartners || [];
      state.totalDocs = payload?.totalDocs || 0;
      if (payload?.globalInventories !== undefined) {
        state.globalInventories = payload.globalInventories;
      }
    },
    updateBlogHomeReadyStatus: (
      state: BlogHomeData,
      { payload }: PayloadAction<"invalid" | "request" | "success" | "failure">,
    ) => {
      state.readyStatus = payload;
    },
    updateBlogHomeError: (state: BlogHomeData, { payload }: PayloadAction<any>) => {
      state.error = payload;
    },
    setActiveCategory: (
      state: BlogHomeData,
      { payload }: PayloadAction<{ name: string; slug: string }>,
    ) => {
      state.activeCategory = payload.name;
      state.activeCategorySlug = payload.slug;
    },
    updateCategoryBlogs: (state: BlogHomeData, { payload }: PayloadAction<BlogPostSimple[]>) => {
      state.categoryBlogs = payload;
      state.categoryBlogsLoading = false;
    },
    setCategoryBlogsLoading: (state: BlogHomeData, { payload }: PayloadAction<boolean>) => {
      state.categoryBlogsLoading = payload;
    },
    resetBlogHomeData: (state: BlogHomeData) => {
      state.readyStatus = "invalid";
      state.homeFeaturedBlogs = [];
      state.sidebarFeaturedBlogs = [];
      state.latestBlogs = [];
      state.categories = [];
      state.activeCategory = "latest";
      state.activeCategorySlug = "";
      state.categoryBlogs = [];
      state.categoryBlogsLoading = false;
      state.blogPartners = [];
      state.totalDocs = 0;
      state.globalInventories = [];
      state.error = null;
    },
  },
});

export const {
  updateBlogHomeData,
  updateBlogHomeReadyStatus,
  updateBlogHomeError,
  setActiveCategory,
  updateCategoryBlogs,
  setCategoryBlogsLoading,
  resetBlogHomeData,
} = blogHomeSlice.actions;

export default blogHomeSlice.reducer;
